import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PartnerPayment {
  id: string;
  partner_id: string;
  period_start: string;
  period_end: string;
  commission_amount: number;
  deals_included: number;
  status: 'pending' | 'processing' | 'paid' | 'cancelled';
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
  created_at: string;
  processed_at?: string;
  paid_at?: string;
}

export interface CreatePaymentData {
  partner_id: string;
  period_start: string;
  period_end: string;
  commission_amount: number;
  deals_included?: number;
  notes?: string;
}

export const usePartnerPayments = (partnerId: string) => {
  return useQuery({
    queryKey: ["partner-payments", partnerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_partner_payments')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching partner payments:", error);
        throw error;
      }
      return data as PartnerPayment[];
    },
    enabled: !!partnerId
  });
};

export const useCreatePartnerPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreatePaymentData) => {
      const { data: result, error } = await supabase
        .from('crm_partner_payments')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to create payment");
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-payments", result.partner_id] });
      toast({
        title: "Success",
        description: "Payment record created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create payment: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdatePartnerPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PartnerPayment> }) => {
      const { data: result, error } = await supabase
        .from('crm_partner_payments')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to update payment");
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-payments", result.partner_id] });
      toast({
        title: "Success",
        description: "Payment updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update payment: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};